import React from 'react';
import { useThemeTokens } from '../../theme/ThemeProvider';

const Progress = ({ 
  value = 0,
  max = 100,
  variant = 'default',
  size = 'medium',
  showLabel = true,
  label,
  animated = false,
  striped = false,
  color,
  className = '',
  style = {},
  ...props 
}) => {
  const { getColor, getSpacing, getFontSize, getBorderRadius, typography } = useThemeTokens();
  
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  
  const getProgressColor = () => {
    if (color) return color;
    
    switch (variant) {
      case 'success':
        return getColor('accent.green');
      case 'warning':
        return getColor('accent.orange');
      case 'danger':
        return getColor('states.error');
      case 'info':
        return getColor('primary.blue');
      default:
        return getColor('primary.blue');
    }
  };
  
  const getProgressHeight = () => {
    switch (size) {
      case 'small':
        return '4px';
      case 'medium':
        return '8px';
      case 'large':
        return '16px';
      default:
        return '8px';
    }
  };
  
  const containerStyles = {
    width: '100%',
    ...style,
  };
  
  const labelStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getSpacing('xs'),
    fontSize: getFontSize('sm'),
    fontWeight: typography.fontWeights.medium,
    color: getColor('neutral.darkGray'),
  };
  
  const trackStyles = {
    width: '100%',
    height: getProgressHeight(),
    backgroundColor: getColor('neutral.lightGray'),
    borderRadius: getBorderRadius('full'),
    overflow: 'hidden',
    position: 'relative',
  };
  
  const barStyles = {
    height: '100%',
    width: `${percentage}%`,
    backgroundColor: getProgressColor(),
    borderRadius: getBorderRadius('full'),
    transition: animated ? 'width 0.3s ease-in-out' : 'none',
    position: 'relative',
    overflow: 'hidden',
  };
  
  const stripedStyles = striped ? {
    backgroundImage: `linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    )`,
    backgroundSize: '1rem 1rem',
    animation: animated ? 'progress-stripes 1s linear infinite' : 'none',
  } : {};
  
  const finalBarStyles = {
    ...barStyles,
    ...stripedStyles,
  };
  
  return (
    <div className={`gaero-progress ${className}`} style={containerStyles} {...props}>
      {showLabel && (
        <div style={labelStyles}>
          <span>{label || ''}</span>
          <span>{Math.round(percentage)}%</span>
        </div>
      )}
      
      <div style={trackStyles} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
        <div style={finalBarStyles} />
      </div>
      
      <style jsx>{`
        @keyframes progress-stripes {
          0% {
            background-position: 1rem 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `}</style>
    </div>
  );
};

// Componente de progreso circular
const CircularProgress = ({
  value = 0,
  max = 100,
  size = 40,
  strokeWidth = 4,
  color,
  variant = 'default',
  showLabel = false,
  label,
  className = '',
  style = {},
  ...props
}) => {
  const { getColor, getFontSize, typography } = useThemeTokens();
  
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const getProgressColor = () => {
    if (color) return color;
    
    switch (variant) {
      case 'success':
        return getColor('accent.green');
      case 'warning':
        return getColor('accent.orange');
      case 'danger':
        return getColor('states.error');
      case 'info':
        return getColor('primary.blue');
      default:
        return getColor('primary.blue');
    }
  };
  
  const containerStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    ...style,
  };
  
  const labelStyles = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size > 60 ? getFontSize('sm') : getFontSize('xs'),
    fontWeight: typography.fontWeights.semibold,
    color: getColor('neutral.darkGray'),
  };
  
  return (
    <div className={`gaero-circular-progress ${className}`} style={containerStyles} {...props}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor('neutral.lightGray')}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getProgressColor()}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.3s ease-in-out',
          }}
        />
      </svg>
      
      {showLabel && (
        <div style={labelStyles}>
          {label || `${Math.round(percentage)}%`}
        </div>
      )}
    </div>
  );
};

Progress.Circular = CircularProgress;

export default Progress;
export { CircularProgress };
